import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  OrganizationTheme,
  CSS_THEME_VARS,
  MATERIAL_COLOR_HUES,
  MATERIAL_COLOR_TYPES,
  MaterialColorHue,
  MaterialColorType,
  ThemeColorPalette,
} from '../models/theme.interface';
import { AVAILABLE_THEMES, DEFAULT_THEME } from '../config/themes';

const MATERIAL_LIGHT_HUES = new Set<MaterialColorHue>(['50', '100', '200', '300', 'A100']);
const MATERIAL_HUE_SOURCE_MAP: Record<MaterialColorHue, keyof ThemeColorPalette> = {
  '50': 'light',
  '100': 'light',
  '200': 'light',
  '300': 'medium',
  '400': 'medium',
  '500': 'main',
  '600': 'dark',
  '700': 'dark',
  '800': 'dark',
  '900': 'dark',
  'A100': 'light',
  'A200': 'main',
  'A400': 'medium',
  'A700': 'dark',
};

/**
 * Theme Service
 *
 * Manages organization-specific theming by:
 * 1. Detecting the current domain
 * 2. Loading the appropriate theme configuration
 * 3. Injecting CSS custom properties into :root
 * 4. Providing theme data to components
 *
 * The theme is loaded once at app initialization and doesn't change
 * during the session (domain-based, not runtime switching).
 */
@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private currentThemeSubject = new BehaviorSubject<OrganizationTheme>(DEFAULT_THEME);
  public currentTheme$: Observable<OrganizationTheme> = this.currentThemeSubject.asObservable();

  constructor() {}

  /**
   * Initialize theme based on current domain
   * Should be called during app initialization (APP_INITIALIZER)
   */
  public initializeTheme(): void {
    const theme = this.detectTheme();
    this.applyTheme(theme);
  }

  /**
   * Get the current theme synchronously
   */
  public get currentTheme(): OrganizationTheme {
    return this.currentThemeSubject.value;
  }

  /**
   * Get the marketing/brand display name for the provided theme (defaults to current)
   */
  public get brandName(): string {
    return this.getBrandName();
  }

  public getBrandName(theme: OrganizationTheme = this.currentTheme): string {
    return theme.brandName || theme.name;
  }

  /**
   * Detect which theme to use based on hostname
   */
  private detectTheme(): OrganizationTheme {
    const hostname = window.location.hostname;
    console.log('[ThemeService] Detecting theme for hostname:', hostname);

    // Find theme where at least one domain matches the current hostname
    const matchedTheme = AVAILABLE_THEMES.find((theme) =>
      theme.domains.some((domain) => hostname.includes(domain))
    );

    if (matchedTheme) {
      console.log('[ThemeService] Matched theme:', matchedTheme.name);
      return matchedTheme;
    }

    console.warn('[ThemeService] No matching theme found, using default:', DEFAULT_THEME.name);
    return DEFAULT_THEME;
  }

  /**
   * Manually switch to a different theme (for debugging/preview purposes)
   */
  public switchTheme(theme: OrganizationTheme): void {
    this.applyTheme(theme);
  }

  /**
   * Apply theme by injecting CSS custom properties and updating state
   */
  private applyTheme(theme: OrganizationTheme): void {
    console.log('[ThemeService] Applying theme:', theme.name);

    this.clearCSSVariables();
    this.injectCSSVariables(theme);
    this.updateFavicon(theme);
    this.currentThemeSubject.next(theme);

    console.log('[ThemeService] Theme applied successfully');
  }

  /**
   * Clear all theme-related CSS variables from :root
   */
  private clearCSSVariables(): void {
    const root = document.documentElement;

    // Clear all CSS theme variables
    Object.values(CSS_THEME_VARS).forEach((varName) => {
      root.style.removeProperty(varName);
    });
    this.clearMaterialPaletteVariables(root);

    console.log('[ThemeService] CSS variables cleared from :root');
  }

  /**
   * Remove dynamically created Angular Material palette variables.
   */
  private clearMaterialPaletteVariables(root: HTMLElement): void {
    MATERIAL_COLOR_TYPES.forEach((type) => {
      MATERIAL_COLOR_HUES.forEach((hue) => {
        root.style.removeProperty(this.getMaterialVarName(type, hue));
        root.style.removeProperty(this.getMaterialVarName(type, hue, true));
      });
    });
  }

  /**
   * Inject all theme values as CSS custom properties into :root
   */
  private injectCSSVariables(theme: OrganizationTheme): void {
    const root = document.documentElement;

    // Primary colors
    this.setProperty(root, CSS_THEME_VARS.PRIMARY, theme.colors.primary.main);
    this.setProperty(root, CSS_THEME_VARS.PRIMARY_LIGHT, theme.colors.primary.light);
    this.setProperty(root, CSS_THEME_VARS.PRIMARY_MEDIUM, theme.colors.primary.medium);
    this.setProperty(root, CSS_THEME_VARS.PRIMARY_DARK, theme.colors.primary.dark);
    this.setProperty(root, CSS_THEME_VARS.PRIMARY_CONTRAST, theme.colors.primary.contrast);

    // Secondary colors
    this.setProperty(root, CSS_THEME_VARS.SECONDARY, theme.colors.secondary.main);
    this.setProperty(root, CSS_THEME_VARS.SECONDARY_LIGHT, theme.colors.secondary.light);
    this.setProperty(root, CSS_THEME_VARS.SECONDARY_MEDIUM, theme.colors.secondary.medium);
    this.setProperty(root, CSS_THEME_VARS.SECONDARY_DARK, theme.colors.secondary.dark);
    this.setProperty(root, CSS_THEME_VARS.SECONDARY_CONTRAST, theme.colors.secondary.contrast);

    // Warn colors
    this.setProperty(root, CSS_THEME_VARS.WARN, theme.colors.warn.main);
    this.setProperty(root, CSS_THEME_VARS.WARN_LIGHT, theme.colors.warn.light);
    this.setProperty(root, CSS_THEME_VARS.WARN_MEDIUM, theme.colors.warn.medium);
    this.setProperty(root, CSS_THEME_VARS.WARN_DARK, theme.colors.warn.dark);

    // Semantic colors
    this.setProperty(root, CSS_THEME_VARS.SUCCESS, theme.colors.success);
    this.setProperty(root, CSS_THEME_VARS.WARNING, theme.colors.warning);
    this.setProperty(root, CSS_THEME_VARS.ERROR, theme.colors.error);
    if (theme.colors.alertLight) {
      this.setProperty(root, CSS_THEME_VARS.ERROR_LIGHT, theme.colors.alertLight);
    }
    this.setProperty(root, CSS_THEME_VARS.INFO, theme.colors.info);
    if (theme.colors.purple) {
      this.setProperty(root, CSS_THEME_VARS.PURPLE, theme.colors.purple);
    }

    // Neutral colors
    this.setProperty(root, CSS_THEME_VARS.WHITE, theme.colors.white);
    this.setProperty(root, CSS_THEME_VARS.BLACK, theme.colors.black);
    this.setProperty(root, CSS_THEME_VARS.GREY_100, theme.colors.grey[100]);
    this.setProperty(root, CSS_THEME_VARS.GREY_300, theme.colors.grey[300]);
    this.setProperty(root, CSS_THEME_VARS.GREY_400, theme.colors.grey[400]);
    this.setProperty(root, CSS_THEME_VARS.GREY_500, theme.colors.grey[500]);
    if (theme.colors.grey[700]) {
      this.setProperty(root, CSS_THEME_VARS.GREY_700, theme.colors.grey[700]);
    }
    this.setProperty(root, CSS_THEME_VARS.GREY_900, theme.colors.grey[900]);
    this.setProperty(root, CSS_THEME_VARS.LIGHTGREY, theme.colors.lightgrey);
    if (theme.colors.whitesmoke) {
      this.setProperty(root, CSS_THEME_VARS.WHITESMOKE, theme.colors.whitesmoke);
    }

    // Text colors
    this.setProperty(root, CSS_THEME_VARS.TEXT_LINK, theme.colors.attachmentLink);
    this.setProperty(root, CSS_THEME_VARS.TEXT_MAIN, theme.colors.text.main);
    this.setProperty(root, CSS_THEME_VARS.TEXT_SUBDUED, theme.colors.text.subdued);
    this.setProperty(root, CSS_THEME_VARS.TEXT_ON_PRIMARY, theme.colors.text.onPrimary);
    this.setProperty(root, CSS_THEME_VARS.TEXT_ON_SECONDARY, theme.colors.text.onSecondary);
    this.setProperty(root, CSS_THEME_VARS.TEXT_ON_WARN, theme.colors.text.onWarn);
    if (theme.colors.text.filterHeading) {
      this.setProperty(root, CSS_THEME_VARS.TEXT_FILTER_HEADING, theme.colors.text.filterHeading);
    }
    if (theme.colors.text.sidebarIcon) {
      this.setProperty(root, CSS_THEME_VARS.TEXT_SIDEBAR_ICON, theme.colors.text.sidebarIcon);
    }
    if (theme.colors.text.sidebarText) {
      this.setProperty(root, CSS_THEME_VARS.TEXT_SIDEBAR_TEXT, theme.colors.text.sidebarText);
    }

    // Background colors
    this.setProperty(root, CSS_THEME_VARS.CARD_BACKGROUND, theme.colors.cardBackground);
    if (theme.colors.pageBackground) {
      this.setProperty(root, CSS_THEME_VARS.PAGE_BACKGROUND, theme.colors.pageBackground);
    }
    if (theme.colors.sidebarBackground) {
      this.setProperty(root, CSS_THEME_VARS.SIDEBAR_BACKGROUND, theme.colors.sidebarBackground);
    }
    if (theme.colors.sidebarOverlay) {
      this.setProperty(root, CSS_THEME_VARS.SIDEBAR_OVERLAY, theme.colors.sidebarOverlay);
    }
    if (theme.colors.rimBackground) {
      this.setProperty(root, CSS_THEME_VARS.RIM_BACKGROUND, theme.colors.rimBackground);
    }
    if (theme.colors.organizationUnitSelectorBackground) {
      this.setProperty(root, CSS_THEME_VARS.ORG_UNIT_SELECTOR_BACKGROUND, theme.colors.organizationUnitSelectorBackground);
    }

    // Angular Material palette tokens derived from the theme
    this.applyMaterialPalettes(root, theme);

    // Font family
    this.setProperty(root, CSS_THEME_VARS.FONT_PRIMARY, theme.fonts.primary);
    this.setProperty(
      root,
      CSS_THEME_VARS.FONT_SECONDARY,
      theme.fonts.secondary || theme.fonts.primary
    );
    if (theme.fonts.logo) {
      this.setProperty(root, CSS_THEME_VARS.FONT_LOGO, theme.fonts.logo);
    }
    this.setProperty(
      root,
      CSS_THEME_VARS.FONT_HEADING,
      theme.fonts.secondary || theme.fonts.primary
    ); // Shortcut for headings
    this.setProperty(root, CSS_THEME_VARS.FONT_BODY, theme.fonts.primary); // Shortcut for body
    this.setProperty(root, CSS_THEME_VARS.FONT_SIDEBAR, theme.fonts.sidebar);
    this.setProperty(root, CSS_THEME_VARS.FONT_CONTENT, theme.fonts.content);

    // Font sizes
    this.setProperty(root, CSS_THEME_VARS.FONT_SIZE_XS, theme.fonts.sizes.xs);
    this.setProperty(root, CSS_THEME_VARS.FONT_SIZE_SM, theme.fonts.sizes.sm);
    this.setProperty(root, CSS_THEME_VARS.FONT_SIZE_BASE, theme.fonts.sizes.base);
    this.setProperty(root, CSS_THEME_VARS.FONT_SIZE_MD, theme.fonts.sizes.md);
    this.setProperty(root, CSS_THEME_VARS.FONT_SIZE_LG, theme.fonts.sizes.lg);
    this.setProperty(root, CSS_THEME_VARS.FONT_SIZE_XL, theme.fonts.sizes.xl);

    // Font weights
    this.setProperty(root, CSS_THEME_VARS.FONT_WEIGHT_LIGHT, theme.fonts.weights.light.toString());
    this.setProperty(root, CSS_THEME_VARS.FONT_WEIGHT_NORMAL, theme.fonts.weights.normal.toString());
    this.setProperty(root, CSS_THEME_VARS.FONT_WEIGHT_MEDIUM, theme.fonts.weights.medium.toString());
    this.setProperty(root, CSS_THEME_VARS.FONT_WEIGHT_SEMIBOLD, theme.fonts.weights.semibold.toString());
    this.setProperty(root, CSS_THEME_VARS.FONT_WEIGHT_BOLD, theme.fonts.weights.bold.toString());

    // Border radius
    this.setProperty(root, CSS_THEME_VARS.BORDER_RADIUS_NONE, theme.ui.borderRadius.none);
    this.setProperty(root, CSS_THEME_VARS.BORDER_RADIUS_SM, theme.ui.borderRadius.sm);
    this.setProperty(root, CSS_THEME_VARS.BORDER_RADIUS_MD, theme.ui.borderRadius.md);
    this.setProperty(root, CSS_THEME_VARS.BORDER_RADIUS_LG, theme.ui.borderRadius.lg);
    this.setProperty(root, CSS_THEME_VARS.BORDER_RADIUS_XL, theme.ui.borderRadius.xl);
    this.setProperty(root, CSS_THEME_VARS.BORDER_RADIUS_FULL, theme.ui.borderRadius.full);

    // Rim width
    if (theme.ui.rimWidth) {
      this.setProperty(root, CSS_THEME_VARS.RIM_WIDTH, theme.ui.rimWidth);
    }

    // Avatar border
    if (theme.ui.avatarBorder !== undefined) {
      this.setProperty(root, CSS_THEME_VARS.AVATAR_BORDER, theme.ui.avatarBorder);
    }

    // Logo styling
    if (theme.ui.logoMaxHeight) {
      this.setProperty(root, CSS_THEME_VARS.LOGO_MAX_HEIGHT, theme.ui.logoMaxHeight);
    }
    if (theme.ui.logoJustify) {
      this.setProperty(root, CSS_THEME_VARS.LOGO_JUSTIFY, theme.ui.logoJustify);
    }

    // Shadows
    if (theme.ui.shadows) {
      this.setProperty(root, CSS_THEME_VARS.SHADOW_LIGHT, theme.ui.shadows.light);
      this.setProperty(root, CSS_THEME_VARS.SHADOW_MEDIUM, theme.ui.shadows.medium);
      this.setProperty(root, CSS_THEME_VARS.SHADOW_HEAVY, theme.ui.shadows.heavy);
    }

    // Assets (for CSS background-image usage)
    if (theme.assets.sidebarBackground && theme.assets.sidebarBackground !== 'none') {
      this.setProperty(root, CSS_THEME_VARS.SIDEBAR_BG, `url('${theme.assets.sidebarBackground}')`);
    } else if (theme.assets.sidebarBackground === 'none') {
      this.setProperty(root, CSS_THEME_VARS.SIDEBAR_BG, 'none');
    }

    if (theme.assets.avatarBackground) {
      this.setProperty(root, CSS_THEME_VARS.AVATAR_BG, `url('${theme.assets.avatarBackground}')`);
    }

    if (theme.assets.avatarImg) {
      this.setProperty(root, CSS_THEME_VARS.AVATAR_IMG, `url('${theme.assets.avatarImg}')`);
    }

    // Transparent overlays
    this.setProperty(root, CSS_THEME_VARS.TRANSPARENT, theme.colors.transparent[0]);
    this.setProperty(root, CSS_THEME_VARS.TRANSPARENT_50, theme.colors.transparent[50]);
    this.setProperty(root, CSS_THEME_VARS.TRANSPARENT_120, theme.colors.transparent[120]);
    this.setProperty(root, CSS_THEME_VARS.TRANSPARENT_140, theme.colors.transparent[140]);
    this.setProperty(root, CSS_THEME_VARS.TRANSPARENT_200, theme.colors.transparent[200]);
    this.setProperty(root, CSS_THEME_VARS.TRANSPARENT_300, theme.colors.transparent[300]);
    this.setProperty(root, CSS_THEME_VARS.TRANSPARENT_400, theme.colors.transparent[400]);
    this.setProperty(root, CSS_THEME_VARS.TRANSPARENT_500, theme.colors.transparent[500]);
    this.setProperty(root, CSS_THEME_VARS.TRANSPARENT_600, theme.colors.transparent[600]);
    this.setProperty(root, CSS_THEME_VARS.TRANSPARENT_700, theme.colors.transparent[700]);
    this.setProperty(root, CSS_THEME_VARS.TRANSPARENT_900, theme.colors.transparent[900]);

    // Status colors
    this.setProperty(root, CSS_THEME_VARS.STATUS_1_BG, theme.colors.status[1].bg);
    this.setProperty(root, CSS_THEME_VARS.STATUS_2_BG, theme.colors.status[2].bg);
    this.setProperty(root, CSS_THEME_VARS.STATUS_3_BG, theme.colors.status[3].bg);
    this.setProperty(root, CSS_THEME_VARS.STATUS_4_BG, theme.colors.status[4].bg);
    this.setProperty(root, CSS_THEME_VARS.STATUS_4_COLOR, theme.colors.status[4].color);
    this.setProperty(root, CSS_THEME_VARS.STATUS_5_BG, theme.colors.status[5].bg);
    this.setProperty(root, CSS_THEME_VARS.STATUS_5_COLOR, theme.colors.status[5].color);

    // Organization types
    this.setProperty(root, CSS_THEME_VARS.BSO, theme.colors.bso);
    this.setProperty(root, CSS_THEME_VARS.KDV, theme.colors.kdv);

    // Message colors
    this.setProperty(root, CSS_THEME_VARS.MESSAGE_SUBTITLE, theme.colors.message.subtitle);
    this.setProperty(root, CSS_THEME_VARS.MESSAGE_PARAGRAPH, theme.colors.message.paragraph);

    // Box shadows
    if (theme.colors.videoBoxShadow) {
      this.setProperty(root, CSS_THEME_VARS.SHADOW_VIDEO, theme.colors.videoBoxShadow);
    }

    console.log('[ThemeService] CSS variables injected into :root');
  }

  /**
   * Update the page favicon based on the active theme.
   */
  private updateFavicon(theme: OrganizationTheme): void {
    const faviconHref = theme.assets.favicon;
    if (!faviconHref) {
      return;
    }

    const iconLinks = Array.from(document.querySelectorAll<HTMLLinkElement>('link[rel*="icon"]'));
    if (!iconLinks.length) {
      const newLink = document.createElement('link');
      newLink.setAttribute('rel', 'icon');
      newLink.setAttribute('href', faviconHref);
      document.head.appendChild(newLink);
      return;
    }

    iconLinks.forEach((link) => {
      link.setAttribute('href', faviconHref);
    });
  }

  /**
   * Helper to set CSS custom property
   */
  private setProperty(element: HTMLElement, property: string, value: string): void {
    element.style.setProperty(property, value);
  }

  /**
   * Generate Angular Material CSS variables for the active theme palette.
   * These feed the legacy Sass-based mat theme so that colors follow the runtime theme.
   */
  private applyMaterialPalettes(root: HTMLElement, theme: OrganizationTheme): void {
    this.setMaterialPalette(
      root,
      'primary',
      theme.colors.primary,
      theme.colors.text.onPrimary,
      theme.colors.text.main
    );
    this.setMaterialPalette(
      root,
      'secondary',
      theme.colors.secondary,
      theme.colors.text.onSecondary,
      theme.colors.text.main
    );
    this.setMaterialPalette(
      root,
      'warn',
      theme.colors.warn,
      theme.colors.text.onWarn,
      theme.colors.text.main
    );
  }

  private setMaterialPalette(
    root: HTMLElement,
    type: MaterialColorType,
    palette: ThemeColorPalette,
    darkContrast: string,
    lightContrast: string
  ): void {
    MATERIAL_COLOR_HUES.forEach((hue) => {
      const colorValue = this.getMaterialHueValue(palette, hue);
      const contrastValue = MATERIAL_LIGHT_HUES.has(hue) ? lightContrast : darkContrast;
      this.setProperty(root, this.getMaterialVarName(type, hue), colorValue);
      this.setProperty(root, this.getMaterialVarName(type, hue, true), contrastValue);
    });
  }

  private getMaterialHueValue(palette: ThemeColorPalette, hue: MaterialColorHue): string {
    const colorKey = MATERIAL_HUE_SOURCE_MAP[hue] || 'main';
    return (palette[colorKey] as string) || palette.main;
  }

  private getMaterialVarName(type: MaterialColorType, hue: MaterialColorHue, contrast = false): string {
    const suffix = contrast ? `contrast-${hue}` : hue;
    return `--theme-material-${type}-${suffix}`;
  }

  /**
   * Get a specific asset URL from the current theme
   */
  public getAsset(key: keyof OrganizationTheme['assets']): string {
    return this.currentTheme.assets[key] || '';
  }

  /**
   * Get logo URL for current theme
   */
  public get logoUrl(): string {
    return this.getAsset('logo');
  }

  /**
   * Get custom logo component for current theme (if defined)
   */
  public get logoComponent(): any {
    return this.currentTheme.assets.logoComponent;
  }

  /**
   * Check if theme has a custom logo component
   */
  public get hasCustomLogoComponent(): boolean {
    return !!this.currentTheme.assets.logoComponent;
  }

  /**
   * Get powered by logo URL for current theme
   */
  public get poweredByLogoUrl(): string {
    return this.getAsset('poweredByLogo');
  }

  /**
   * Get sidebar background URL for current theme
   */
  public get sidebarBackgroundUrl(): string | undefined {
    return this.getAsset('sidebarBackground');
  }

  /**
   * Get activity placeholder URL for current theme
   */
  public get activityPlaceholderUrl(): string {
    return this.getAsset('activityPlaceholder');
  }

  /**
   * Get avatar image URL for current theme
   */
  public get avatarImgUrl(): string | undefined {
    return this.getAsset('avatarImg');
  }

  /**
   * Get activity blocked image URL for current theme
   */
  public get activityBlockedImageUrl(): string {
    return this.getAsset('activityBlockedImage') || './assets/images/activities/activity-blocked.png';
  }

  /**
   * Get activity 404 image URL for current theme
   */
  public get activity404ImageUrl(): string {
    return this.getAsset('activity404Image') || './assets/images/activities/activity-404.jpg';
  }
}
